import { useMemo } from "react";
import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useCadastroPesquisaListaViewModel } from "../../viewModels/cadastro_pesquisa_lista_view_model";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { GenerateCadastroPesquisaListaColumnDefinition } from "./columns";
import { PesquisaSatisfacaoLista } from "../../../domain/models/pesquisa_satisfacao_lista";
import { PesquisaSatisfacaoModal } from "./components/pesquisa_satisfacao_modal";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";

import AlertError from "../../../../../common/components/alert_error";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * CadastroPesquisaLista
 */

export const CadastroPesquisaLista = () => {
  const viewModel = useCadastroPesquisaListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateCadastroPesquisaListaColumnDefinition({
      onActionEditar: viewModel.handlePesquisaSatisfacaoModalOpenState,
      onActionDelete: viewModel.handlePesquisaSatisfacaoDeletarModalOpenState,
      onActionGoToItens: viewModel.handleGoToPesquisaSatisfacaoItens,
    });
  }, [
    viewModel.handlePesquisaSatisfacaoModalOpenState,
    viewModel.handlePesquisaSatisfacaoDeletarModalOpenState,
    viewModel.handleGoToPesquisaSatisfacaoItens,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[115px]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    viewModel.handlePesquisaSatisfacaoModalOpenState(null, true);
                    e.preventDefault();
                  }}>
                  Novo Item
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Entrega de Obras
          </Link>
          <Typography className="text-primary-green">
            Cadastro de Pesquisa de Satisfação
          </Typography>
        </Breadcrumbs>

        <div className="flex flex-col gap-0">
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-2xl font-medium text-black dark:text-white">
                Cadastro de Pesquisa de Satisfação
              </h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-start flex gap-2">
            </div>
          </div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  field={viewModel.filters.field}
                  value={viewModel.filters.value}
                  status={viewModel.filters.status}
                  onFieldChange={viewModel.handleFieldChange}
                  onValueChange={viewModel.handleValueChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onClearFilters={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                  <StyledDataGrid
                    tableName="cadastro_pesquisa_lista"
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={false}
                    getRowId={(row: PesquisaSatisfacaoLista) => row.idPesquisaSatisfacao}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    rows={viewModel.pesquisasSatisfacao}
                    rowCount={viewModel.pesquisasSatisfacao.length}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />
                </Stack>
              </>
            )
        }
      </div>

      {/**
        * Modals
        */}

      <PesquisaSatisfacaoModal
        open={viewModel.pesquisaSatisfacaoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedPesquisaSatisfacao={viewModel.selectedPesquisaSatisfacao}
        onClose={() => viewModel.handlePesquisaSatisfacaoModalOpenState(null, false)}
        onSubmit={viewModel.handlePesquisaSatisfacaoSubmit}
        onOpenChangeHistory={viewModel.handleOpenChangeHistory}
      />

      <DeleteDialog
        open={viewModel.pesquisaSatisfacaoDeleteModal}
        saving={viewModel.saving}
        title="Você realmente deseja excluir este item?"
        onClose={() => viewModel.handlePesquisaSatisfacaoDeletarModalOpenState(null, false)}
        onDelete={() => viewModel.handlePesquisaSatisfacaoDeleteSubmit()}
      />

      <ChangeHistoryModal
        changeHistoryHandler={viewModel.changeHistory}
      />
    </>
  );
}